import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, ShoppingCart, ArrowLeft, Check } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColour, setSelectedColour] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        if (response.data.sizes?.length > 0) {
          setSelectedSize(response.data.sizes[0]);
        }
        if (response.data.colours?.length > 0) {
          setSelectedColour(response.data.colours[0]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = () => {
    if (!product.isActive || product.stockQty === 0) {
      toast.error('This product is currently unavailable');
      return;
    }
    if (product.sizes?.length > 0 && !selectedSize) {
      toast.error('Please select a size');
      return;
    }
    if (product.colours?.length > 0 && !selectedColour) {
      toast.error('Please select a colour');
      return;
    }

    addToCart(product, quantity, selectedSize, selectedColour);
    setAddedToCart(true);
    toast.success('Added to cart!');
    
    setTimeout(() => setAddedToCart(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="animate-pulse bg-zinc-100 aspect-square" />
            <div className="space-y-4">
              <div className="h-8 bg-zinc-100 rounded w-3/4" />
              <div className="h-4 bg-zinc-100 rounded w-full" />
              <div className="h-4 bg-zinc-100 rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-zinc-50 py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/shop" 
            className="inline-flex items-center gap-2 text-zinc-600 hover:text-black transition-colors text-sm"
            data-testid="back-to-shop"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Shop
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div 
              className="aspect-square bg-zinc-100 overflow-hidden"
              data-testid="product-image"
            >
              <img
                src={product.imageUrl}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-col"
          >
            {product.category && (
              <span className="text-[#ff4d6d] font-bold text-xs uppercase tracking-widest mb-2">
                {product.category}
              </span>
            )}
            
            <h1 
              className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight"
              data-testid="product-name"
            >
              {product.name}
            </h1>
            
            <p 
              className="text-3xl font-bold mt-4"
              data-testid="product-price"
            >
              ${product.price.toFixed(2)} <span className="text-zinc-400 text-lg font-normal">AUD</span>
            </p>

            <p className="text-zinc-600 mt-6 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Size Selection */}
            {product.sizes?.length > 0 && (
              <div className="mt-8">
                <label className="font-bold text-sm uppercase tracking-wider block mb-3">
                  Size
                </label>
                <div className="flex flex-wrap gap-2" data-testid="size-options">
                  {product.sizes.map(size => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 border-2 font-bold text-sm uppercase transition-all ${
                        selectedSize === size
                          ? 'border-black bg-black text-white'
                          : 'border-zinc-200 hover:border-black'
                      }`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Colour Selection */}
            {product.colours?.length > 0 && (
              <div className="mt-6">
                <label className="font-bold text-sm uppercase tracking-wider block mb-3">
                  Colour
                </label>
                <div className="flex flex-wrap gap-2" data-testid="colour-options">
                  {product.colours.map(colour => (
                    <button
                      key={colour}
                      onClick={() => setSelectedColour(colour)}
                      className={`px-4 py-2 border-2 font-medium text-sm transition-all ${
                        selectedColour === colour
                          ? 'border-black bg-black text-white'
                          : 'border-zinc-200 hover:border-black'
                      }`}
                      data-testid={`colour-${colour}`}
                    >
                      {colour}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mt-6">
              <label className="font-bold text-sm uppercase tracking-wider block mb-3">
                Quantity
              </label>
              <div className="flex items-center gap-4" data-testid="quantity-selector">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 border-2 border-zinc-200 flex items-center justify-center hover:border-black transition-colors"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-bold text-lg w-8 text-center" data-testid="quantity-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 border-2 border-zinc-200 flex items-center justify-center hover:border-black transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              {product.stockQty === 0 ? (
                <Button
                  disabled
                  className="flex-1 h-14 text-base font-bold uppercase tracking-wider rounded-full bg-zinc-300 text-zinc-500 cursor-not-allowed"
                  data-testid="sold-out-btn"
                >
                  Sold Out
                </Button>
              ) : (
                <Button
                  onClick={handleAddToCart}
                  className={`flex-1 h-14 text-base font-bold uppercase tracking-wider rounded-full transition-all ${
                    addedToCart
                      ? 'bg-[#00ff9d] text-black hover:bg-[#00ff9d]'
                      : 'bg-[#09090b] text-white hover:bg-[#00ff9d] hover:text-black'
                  }`}
                  data-testid="add-to-cart-btn"
                >
                  {addedToCart ? (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Added to Cart
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Add to Cart
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Stock Info */}
            <p className={`text-sm mt-4 ${product.stockQty === 0 ? 'text-[#ff4d6d] font-bold' : 'text-zinc-500'}`}>
              {product.stockQty === 0 
                ? 'SOLD OUT' 
                : product.stockQty <= 10 
                  ? `Only ${product.stockQty} left!` 
                  : 'In Stock'}
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
